/**
 * Credits Purchase Screen
 * Allows users to purchase appointment credits
 * Supports both Web (Stripe) and Mobile (IAP) payments
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
  SafeAreaView,
  Modal,
  Linking,
} from 'react-native';
// Conditional import for Stripe - use web version on web, native on mobile
let CardField: any = null;
let useStripe: any = null;

if (Platform.OS === 'web') {
  // On web, we use Stripe Checkout (redirect-based), so we don't need to load Stripe.js
  // This avoids webpack module resolution issues with Stripe's locale files
  useStripe = () => {
    // Return null - we don't need Stripe instance for Checkout redirect
    return null;
  };
  // CardField is not needed on web - we use Stripe Checkout redirect
  CardField = () => null;
} else {
  // On mobile, use @stripe/stripe-react-native
  try {
    const stripeNative = require('@stripe/stripe-react-native');
    CardField = stripeNative.CardField;
    useStripe = stripeNative.useStripe;
  } catch (error) {
    console.warn('@stripe/stripe-react-native not available:', error);
    useStripe = () => null;
    CardField = () => null;
  }
}
import creditsService, { CreditPack, UserCredits } from '../services/creditsService';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import { AuthManager } from '../utils/authManager';

// API base URL for web requests - use same as creditsService
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_CREDITS_API_URL || process.env.REACT_APP_API_URL || 'https://bookapt-user-credits.onrender.com';

// Conditional import for react-native-iap (mobile only)
let RNIap: any = null;
let initConnection: any = null;
let purchaseErrorListener: any = null;
let purchaseUpdatedListener: any = null;
let getAvailablePurchases: any = null;
let Product: any = null;

if (Platform.OS !== 'web') {
  try {
    const iapModule = require('react-native-iap');
    RNIap = iapModule.default ?? iapModule; // v14 has no default export, use module
    initConnection = iapModule.initConnection;
    Product = iapModule.Product;
    purchaseErrorListener = iapModule.purchaseErrorListener;
    purchaseUpdatedListener = iapModule.purchaseUpdatedListener;
    getAvailablePurchases = iapModule.getAvailablePurchases;
  } catch (error) {
    console.warn('react-native-iap not available:', error);
  }
}

interface CreditsPurchaseScreenProps {
  onBack: () => void;
  onPurchaseSuccess?: (credits: UserCredits) => void;
}

const CreditsPurchaseScreen: React.FC<CreditsPurchaseScreenProps> = ({
  onBack,
  onPurchaseSuccess,
}) => {
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [userCredits, setUserCredits] = useState<UserCredits | null>(null);
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showCardInput, setShowCardInput] = useState(false);
  const [isWeb, setIsWeb] = useState(false);

  // On web, we don't use Stripe.js - we redirect to Stripe Checkout
  // So we don't need to initialize Stripe instance
  const stripeHook = Platform.OS === 'web' ? null : (useStripe ? useStripe() : null);
  const stripe = stripeHook;
  const confirmPayment = stripeHook?.confirmPayment || (async () => ({ error: null }));

  // Mobile IAP product IDs
  const IAP_PRODUCT_IDS = [
    'com.bookapt.credits.pack20',
    'com.bookapt.credits.pack40',
    'com.bookapt.credits.pack100',
  ];

  useEffect(() => {
    setIsWeb(Platform.OS === 'web');
    loadData();
    
    // Check for Stripe Checkout success/cancel redirect (web only)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const canceled = urlParams.get('canceled');
      const sessionId = urlParams.get('session_id');
      
      if (success === 'true' && sessionId) {
        // Verify payment actually succeeded before adding credits
        setTimeout(async () => {
          try {
            // Verify the checkout session was actually paid
            const verifyResponse = await fetch(`${API_BASE_URL}/api/user/credits/checkout/verify/${sessionId}`);
            const verifyData = await verifyResponse.json();
            
            if (verifyData.success) {
              // Payment verified - force refresh credits immediately
              console.log('✅ CreditsPurchaseScreen: Payment verified, refreshing credits...');
              
              // Clear cache first
              await creditsService.clearCreditsCache();
              
              // Also try to update Firestore directly using firebaseService
              try {
                const { firebaseService } = await import('../services/firebaseService');
                const user = await UnifiedAuthService.getCurrentUser();
                const userId = user?.uid || user?.id;
                if (userId && verifyData.creditsAdded) {
                  // Add credits directly to Firestore for immediate update
                  await firebaseService.addCredits(userId, verifyData.creditsAdded);
                  console.log('✅ CreditsPurchaseScreen: Credits added to Firestore directly');
                }
              } catch (firestoreError) {
                console.warn('⚠️ CreditsPurchaseScreen: Could not update Firestore directly:', firestoreError);
                // Continue anyway - backend already added credits
              }
              
              // Force refresh multiple times to ensure UI updates
              await loadUserCredits(true); // Force refresh from Firestore/API
              setTimeout(() => loadUserCredits(true), 1000); // Refresh again after 1 second
              setTimeout(() => loadUserCredits(true), 3000); // Refresh again after 3 seconds
              
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
              if (Platform.OS === 'web') {
                window.alert(`Success! ${verifyData.creditsAdded} credits have been added. If balance doesn't update, click the Refresh button.`);
              } else {
                Alert.alert('Success', `Your ${verifyData.creditsAdded} credits have been added to your account!`);
              }
            } else {
              // Payment not completed - show error
              console.error('Payment verification failed:', verifyData);
              window.history.replaceState({}, '', window.location.pathname);
              if (Platform.OS === 'web') {
                window.alert('Payment not completed. Please try again or contact support if you were charged.');
              } else {
                Alert.alert('Payment Error', 'Payment was not completed. Please try again.');
              }
            }
          } catch (error) {
            console.error('Error verifying payment:', error);
            // Clean URL anyway
            window.history.replaceState({}, '', window.location.pathname);
            if (Platform.OS === 'web') {
              window.alert('Unable to verify payment. Credits will be added automatically if payment was successful.');
            } else {
              Alert.alert('Verification Error', 'Unable to verify payment. Please check your credits balance.');
            }
          }
        }, 500);
      } else if (canceled === 'true') {
        // Payment canceled
        setTimeout(() => {
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        }, 500);
      }
    }
    
    // Initialize IAP store connection and set up listeners for mobile
    if (!isWeb && RNIap && initConnection && purchaseUpdatedListener && purchaseErrorListener) {
      let cancelled = false;
      (async () => {
        try {
          await initConnection();
          if (!cancelled) setupIAPListeners();
        } catch (e) {
          console.warn('IAP initConnection failed:', e);
        }
      })();
      return () => {
        cancelled = true;
        try {
          if (purchaseUpdatedListener) purchaseUpdatedListener.remove();
          if (purchaseErrorListener) purchaseErrorListener.remove();
        } catch (error) {
          console.warn('Error removing IAP listeners:', error);
        }
      };
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 CreditsPurchaseScreen: Loading credit packs and user credits...');
      
      const [packs, credits] = await Promise.all([
        creditsService.getCreditPacks(),
        loadUserCredits(),
      ]);
      
      console.log('✅ CreditsPurchaseScreen: Loaded credit packs:', packs.length);
      console.log('✅ CreditsPurchaseScreen: Credit packs:', packs);
      
      if (!packs || packs.length === 0) {
        console.warn('⚠️ CreditsPurchaseScreen: No credit packs loaded, showing error');
        if (Platform.OS === 'web') {
          window.alert('No credit packages available. Please refresh the page or contact support.');
        } else {
          Alert.alert('Error', 'No credit packages available. Please try again or contact support.');
        }
      }
      
      setCreditPacks(packs || []);
    } catch (error: any) {
      console.error('❌ CreditsPurchaseScreen: Error loading data:', error);
      console.error('❌ Error details:', error.message, error.stack);
      
      // Even if there's an error, try to show default packages
      try {
        const defaultPacks = await creditsService.getCreditPacks();
        if (defaultPacks && defaultPacks.length > 0) {
          console.log('✅ CreditsPurchaseScreen: Using default packages after error');
          setCreditPacks(defaultPacks);
        } else {
          if (Platform.OS === 'web') {
            window.alert('Failed to load credit packages. Please refresh the page or contact support.');
          } else {
            Alert.alert('Error', 'Failed to load credit packages. Please try again or contact support.');
          }
        }
      } catch (fallbackError) {
        console.error('❌ CreditsPurchaseScreen: Fallback also failed:', fallbackError);
        if (Platform.OS === 'web') {
          window.alert('Unable to load credit packages. Please check your connection and try again.');
        } else {
          Alert.alert('Error', 'Unable to load credit packages. Please check your connection and try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUserCredits = async (forceRefresh: boolean = false) => {
    try {
      let user: any = null;
      let userId: string | undefined;
      
      // Use UnifiedAuthService on web, AuthManager on mobile
      if (Platform.OS === 'web') {
        user = await UnifiedAuthService.getCurrentUser();
        userId = user?.uid || user?.id;
      } else {
        // On mobile, prefer Firebase UID (same as web) and only use AuthManager as last resort
        try {
          user = await UnifiedAuthService.getCurrentUser();
          userId = user?.uid || user?.id;
        } catch (_) {
          /* ignore */
        }

        if (!userId) {
          try {
            const authModule = require('@react-native-firebase/auth');
            userId = authModule?.default?.()?.currentUser?.uid;
          } catch (_) {
            /* ignore */
          }
        }

        if (!userId) {
          try {
            user = await AuthManager.getSavedUser();
            userId = user?.id;
            if (userId) {
              console.warn('⚠️ CreditsPurchaseScreen: Using legacy AuthManager userId (may not match Firebase UID)');
            }
          } catch (_) {
            /* ignore */
          }
        }
      }
      
      if (userId) {
        // Force refresh to get latest from Firestore/API
        const credits = await creditsService.getUserCredits(userId, forceRefresh);
        setUserCredits(credits);
        console.log('✅ CreditsPurchaseScreen: Credits loaded - Available:', credits.availableCredits);
        return credits;
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
    }
    return null;
  };

  const setupIAPListeners = () => {
    if (!RNIap || Platform.OS === 'web') {
      return; // Skip IAP setup on web
    }
    // Listen for successful purchases
    purchaseUpdatedListener((purchase) => {
      handleMobilePurchaseSuccess(purchase);
    });

    // Listen for purchase errors
    purchaseErrorListener((error) => {
      console.error('Purchase error:', error);
      setPurchasing(false);
      Alert.alert('Purchase Failed', error.message || 'Failed to complete purchase');
    });
  };

  const handlePackSelect = (pack: CreditPack) => {
    setSelectedPack(pack);
    setShowCardInput(false); // Payment options will be shown instead
  };

  const handleMobilePurchase = async (pack: CreditPack) => {
    try {
      setPurchasing(true);

      // Map pack ID to IAP product ID
      const productIdMap: { [key: string]: string } = {
        pack20: 'com.bookapt.credits.pack20',
        pack40: 'com.bookapt.credits.pack40',
        pack100: 'com.bookapt.credits.pack100',
      };

      const productId = productIdMap[pack.id];
      if (!productId) {
        throw new Error('Invalid pack ID');
      }

      // Request purchase (IAP only on native mobile)
      if (!RNIap || Platform.OS === 'web') {
        setPurchasing(false);
        return;
      }
      // react-native-iap v14 API: request object with platform-specific sku
      await RNIap.requestPurchase({
        request: {
          apple: { sku: productId },
          google: { skus: [productId] },
        },
        type: 'in-app',
      });
    } catch (error: any) {
      console.error('Purchase error:', error);
      setPurchasing(false);
      Alert.alert('Purchase Failed', error.message || 'Failed to initiate purchase');
    }
  };

  const handleRestorePurchases = async () => {
    if (!RNIap || Platform.OS === 'web' || !getAvailablePurchases) {
      if (Platform.OS !== 'web') {
        Alert.alert(
          'Restore Unavailable',
          'In-app purchase restore is not available. Please update the app from the App Store.'
        );
      }
      return;
    }
    try {
      setRestoring(true);
      const purchases = await getAvailablePurchases();
      const productIdMap: { [key: string]: string } = {
        'com.bookapt.credits.pack20': 'pack20',
        'com.bookapt.credits.pack40': 'pack40',
        'com.bookapt.credits.pack100': 'pack100',
      };
      const ourProductIds = Object.keys(productIdMap);
      const creditPurchases = Array.isArray(purchases)
        ? purchases.filter((p: any) => ourProductIds.includes(p.productId || p.productID))
        : [];
      let restored = 0;
      for (const purchase of creditPurchases) {
        const productId = purchase.productId || purchase.productID;
        try {
          let user: any = null;
          let userId: string | undefined;
          if (Platform.OS === 'web') {
            user = await UnifiedAuthService.getCurrentUser();
            userId = user?.uid || user?.id;
          } else {
            try {
              user = await UnifiedAuthService.getCurrentUser();
              userId = user?.uid || user?.id;
            } catch (_) {}
            if (!userId) {
              try {
                const authModule = require('@react-native-firebase/auth');
                userId = authModule?.default?.()?.currentUser?.uid;
              } catch (_) {}
            }
            if (!userId) {
              user = await AuthManager.getSavedUser();
              userId = user?.id;
            }
          }
          if (!userId) continue;
          const result = await creditsService.processMobilePurchase(
            userId,
            productId,
            purchase.transactionId || purchase.transactionReceipt,
            Platform.OS === 'ios' ? 'ios' : 'android'
          );
          if (result.success) {
            if (RNIap && RNIap.finishTransaction) {
              await RNIap.finishTransaction({ purchase, isConsumable: true });
            }
            restored++;
          }
        } catch (e) {
          console.warn('Error processing restored purchase:', e);
        }
      }
      await loadUserCredits(true);
      if (restored > 0) {
        Alert.alert('Restore Complete', `${restored} purchase(s) restored. Your credits have been updated.`);
      } else {
        Alert.alert('No Purchases to Restore', 'No previous credit purchases were found for this account.');
      }
    } catch (error: any) {
      console.error('Restore error:', error);
      Alert.alert('Restore Failed', error?.message || 'Unable to restore purchases. Please try again.');
    } finally {
      setRestoring(false);
    }
  };

  const handleMobilePurchaseSuccess = async (purchase: any) => {
    try {
      // Get user based on platform
      let user: any = null;
      let userId: string | undefined;
      if (Platform.OS === 'web') {
        user = await UnifiedAuthService.getCurrentUser();
        userId = user?.uid || user?.id;
      } else {
        try {
          user = await UnifiedAuthService.getCurrentUser();
          userId = user?.uid || user?.id;
        } catch (_) {
          /* ignore */
        }

        if (!userId) {
          try {
            const authModule = require('@react-native-firebase/auth');
            userId = authModule?.default?.()?.currentUser?.uid;
          } catch (_) {
            /* ignore */
          }
        }

        if (!userId) {
          user = await AuthManager.getSavedUser();
          userId = user?.id;
          if (userId) {
            console.warn('⚠️ CreditsPurchaseScreen: Using legacy AuthManager userId (may not match Firebase UID)');
          }
        }
      }
      
      if (!userId) {
        throw new Error('User not found');
      }

      // Process purchase with backend
      const result = await creditsService.processMobilePurchase(
        userId,
        purchase.productId,
        purchase.transactionId || purchase.transactionReceipt,
        Platform.OS === 'ios' ? 'ios' : 'android'
      );

      if (result.success) {
        // Finish the transaction (credits are consumable)
        if (RNIap && Platform.OS !== 'web') {
          await RNIap.finishTransaction({ purchase, isConsumable: true });
        }

        // Refresh credits
        const credits = await loadUserCredits();
        
        Alert.alert(
          'Success!',
          `You've successfully purchased ${result.purchase?.creditsAdded} credits!`,
          [
            {
              text: 'OK',
              onPress: () => {
                setPurchasing(false);
                if (onPurchaseSuccess && credits) {
                  onPurchaseSuccess(credits);
                }
              },
            },
          ]
        );
      } else {
        throw new Error(result.error || 'Failed to process purchase');
      }
    } catch (error: any) {
      console.error('Error processing purchase:', error);
      setPurchasing(false);
      Alert.alert('Error', error.message || 'Failed to process purchase');
    }
  };

  const handleWalletPayment = async (walletType: 'apple_pay' | 'google_pay' | 'paypal') => {
    if (!selectedPack) {
      Alert.alert('Error', 'Please select a credit pack');
      return;
    }

    setPurchasing(true);

    try {
      // Get user
      let user: any = null;
      if (Platform.OS === 'web') {
        user = await UnifiedAuthService.getCurrentUser();
      } else {
        try {
          user = await UnifiedAuthService.getCurrentUser();
        } catch (e) {
          user = await AuthManager.getSavedUser();
        }
      }
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const userId = user.uid || user.id;
      if (!userId) {
        throw new Error('Unable to identify user');
      }

      // Create payment intent for wallet payment
      const purchase = await creditsService.createCreditPurchase(
        selectedPack.id,
        userId,
        user.email || '',
        user.name || user.displayName || undefined
      );

      // Process wallet payment based on type
      let paymentResult;
      
      if (walletType === 'paypal') {
        // Use Braintree for PayPal payment
        try {
          const { braintreeService } = require('../services/braintreeService');
          
          // Get payment method nonce from Braintree
          const braintreeResult = await braintreeService.processPayPalPayment({
            amount: selectedPack.price,
            serviceName: `${selectedPack.credits} Credits`,
            customerId: userId,
            businessId: 'bookapt_platform',
            description: `Purchase ${selectedPack.credits} appointment credits`,
          });

          if (!braintreeResult.success || !braintreeResult.paymentMethodNonce) {
            throw new Error(braintreeResult.error || 'Failed to get PayPal payment method');
          }

          // Process payment with backend
          const processResponse = await fetch(`${API_BASE_URL}/api/user/credits/braintree-process`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId,
              packId: selectedPack.id,
              paymentMethodNonce: braintreeResult.paymentMethodNonce,
              userEmail: user.email || '',
              userName: user.name || user.displayName || undefined,
            }),
          });

          const processData = await processResponse.json();

          if (!processData.success || !processData.transactionId) {
            throw new Error(processData.error || 'Failed to process PayPal payment');
          }

          // Now verify and add credits using wallet-purchase endpoint
          paymentResult = {
            success: true,
            transactionId: processData.transactionId,
          };
        } catch (paypalError: any) {
          console.error('PayPal payment error:', paypalError);
          setPurchasing(false);
          
          // Show helpful error message for PayPal linking issue
          let errorMessage = paypalError.message || 'Please try again or use card payment.';
          if (paypalError.errorCode === 'PAYPAL_SANDBOX_ACCOUNT_NOT_LINKED' || paypalError.errorCode === 'PAYPAL_ACCOUNT_NOT_LINKED' || paypalError.message?.includes('linked PayPal')) {
            const isProduction = !paypalError.errorCode?.includes('SANDBOX');
            const accountType = isProduction ? 'PayPal Business' : 'PayPal Sandbox';
            errorMessage = `${accountType} account needs to be linked to Braintree.\n\nTo fix:\n1. Go to Braintree Dashboard (production: braintreegateway.com)\n2. Settings → Processing → PayPal\n3. Click "Link PayPal Account"\n4. Follow the instructions\n\nOr use "Pay with Card" for now.`;
          }
          
          if (Platform.OS === 'web') {
            window.alert(`PayPal payment failed:\n\n${errorMessage}`);
          } else {
            Alert.alert('PayPal Payment Failed', errorMessage);
          }
          return;
        }
      } else {
        // Use Stripe for Apple Pay / Google Pay
        // This would use Stripe's Payment Request API
        paymentResult = { success: true, transactionId: `wallet_${walletType}_${Date.now()}` };
      }

      if (paymentResult.success) {
        console.log('Payment result success, processing wallet purchase...', {
          userId,
          packId: selectedPack.id,
          transactionId: paymentResult.transactionId,
          walletType
        });
        
        // Confirm the purchase with backend using wallet purchase endpoint
        try {
          const result = await creditsService.processWalletPurchase(
            userId,
            selectedPack.id,
            paymentResult.transactionId || `wallet_${walletType}_${Date.now()}`,
            walletType
          );

          console.log('Wallet purchase result:', result);

          if (result.success) {
            // Force refresh credits immediately
            console.log('✅ CreditsPurchaseScreen: Wallet purchase successful, refreshing credits...');
            await loadUserCredits(true); // Force refresh
            
            // Also try to update Firestore directly for immediate reflection
            try {
              const { firebaseService } = await import('../services/firebaseService');
              const user = await UnifiedAuthService.getCurrentUser();
              const userId = user?.uid || user?.id;
              if (userId && result.purchase?.creditsAdded) {
                await firebaseService.addCredits(userId, result.purchase.creditsAdded);
                console.log('✅ CreditsPurchaseScreen: Credits added to Firestore directly');
                // Reload again to get updated balance
                await loadUserCredits(true);
              }
            } catch (firestoreError) {
              console.warn('⚠️ CreditsPurchaseScreen: Could not update Firestore directly:', firestoreError);
              // Continue anyway - backend already added credits
            }
            
            // Show success message
            if (Platform.OS === 'web') {
              window.alert(
                `Payment Successful!\n\nYour ${selectedPack.credits} credits have been added using ${walletType === 'apple_pay' ? 'Apple Pay' : walletType === 'google_pay' ? 'Google Pay' : 'PayPal'}.`
              );
            } else {
              Alert.alert(
                'Payment Successful!',
                `Your ${selectedPack.credits} credits have been added using ${walletType === 'apple_pay' ? 'Apple Pay' : walletType === 'google_pay' ? 'Google Pay' : 'PayPal'}.`,
                [
                  {
                    text: 'Continue',
                    onPress: () => {
                      setSelectedPack(null);
                      setPurchasing(false);
                    }
                  }
                ]
              );
            }
            
            setSelectedPack(null);
            setPurchasing(false);
          } else {
            throw new Error(result.error || 'Failed to process purchase');
          }
        } catch (backendError: any) {
          console.error('Backend wallet purchase error:', backendError);
          throw new Error(backendError.message || 'Failed to process purchase with backend');
        }
      } else {
        throw new Error(paymentResult.error || 'Payment failed');
      }
    } catch (error: any) {
      console.error('Wallet payment error:', error);
      setPurchasing(false);
      Alert.alert('Payment Failed', error.message || 'Failed to process payment');
    }
  };

  const handleWebPurchase = async () => {
    if (!selectedPack) {
      Alert.alert('Error', 'Please select a credit pack');
      setPurchasing(false);
      return;
    }

    // On web, we don't need Stripe instance - we use redirect to Stripe Checkout
    // No need to check for stripe object

    try {
      setPurchasing(true);

      // Get user
      let user: any = null;
      if (Platform.OS === 'web') {
        user = await UnifiedAuthService.getCurrentUser();
      } else {
        try {
          user = await UnifiedAuthService.getCurrentUser();
        } catch (e) {
          user = await AuthManager.getSavedUser();
        }
      }
      
      if (!user) {
        throw new Error('User not found. Please log in again.');
      }
      
      const userId = user.uid || user.id;
      if (!userId) {
        throw new Error('Unable to identify user');
      }

      // For web, go directly to Stripe Checkout (no payment intent needed)
      // Stripe Checkout handles everything: Apple Pay, Google Pay, Cards
      try {
        // Create Stripe Checkout session and redirect
        console.log('🔍 CreditsPurchaseScreen: Creating Stripe Checkout session...', {
          packId: selectedPack.id,
          userId: userId,
          userEmail: user.email,
          API_BASE_URL
        });

        const baseUrl = Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://bookapt.io';
        const isMobile = Platform.OS !== 'web';
        const maxAttempts = isMobile ? 3 : 1;
        const retryDelayMs = 2500;

        let checkoutResponse: Response;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s per attempt
          try {
            checkoutResponse = await fetch(`${API_BASE_URL}/api/user/credits/checkout/create`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                packId: selectedPack.id,
                userId: userId,
                userEmail: user.email || '',
                userName: user.name || user.displayName || '',
                successUrl: `${baseUrl}/credits?success=true`,
                cancelUrl: `${baseUrl}/credits?canceled=true`,
              }),
              signal: controller.signal,
            });
            clearTimeout(timeoutId);
            break;
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            const isNetworkError =
              fetchError?.message === 'Network request failed' ||
              fetchError?.message?.includes('NetworkError') ||
              fetchError?.message?.includes('Failed to fetch');
            if (attempt < maxAttempts && isNetworkError) {
              console.warn(
                `CreditsPurchaseScreen: Checkout attempt ${attempt}/${maxAttempts} failed (${fetchError?.message}), retrying in ${retryDelayMs}ms...`
              );
              await new Promise((r) => setTimeout(r, retryDelayMs));
              continue;
            }
            if (fetchError.name === 'AbortError') {
              throw new Error('Request timed out. The server may be slow or unreachable. Please try again or contact support.');
            }
            throw fetchError;
          }
        }

        console.log('🔍 CreditsPurchaseScreen: Checkout response status:', checkoutResponse.status);

        if (!checkoutResponse.ok) {
          const errorText = await checkoutResponse.text();
          let errorMessage = `Server error: ${checkoutResponse.status}`;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorMessage;
          } catch {
            errorMessage = errorText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const checkoutData = await checkoutResponse.json();
        console.log('🔍 CreditsPurchaseScreen: Checkout response data:', checkoutData);
        
        if (checkoutData.success && checkoutData.sessionUrl) {
          const sessionUrl = checkoutData.sessionUrl;
          console.log('✅ CreditsPurchaseScreen: Opening Stripe Checkout:', sessionUrl);
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.location.href = sessionUrl;
          } else {
            const canOpen = await Linking.canOpenURL(sessionUrl);
            if (canOpen) {
              await Linking.openURL(sessionUrl);
            } else {
              throw new Error('Could not open payment page');
            }
            setPurchasing(false);
          }
          return;
        } else {
          const errorMsg = checkoutData.error || 'Failed to create checkout session';
          console.error('❌ CreditsPurchaseScreen: Checkout session creation failed:', errorMsg);
          throw new Error(errorMsg);
        }
      } catch (checkoutError: any) {
        console.error('❌ CreditsPurchaseScreen: Checkout error:', checkoutError);
        console.error('❌ Error details:', {
          message: checkoutError.message,
          stack: checkoutError.stack,
          name: checkoutError.name
        });
        
        setPurchasing(false);
        
        // Detect CORS errors and provide helpful message
        const isCorsError = checkoutError.message?.includes('CORS') || 
                           checkoutError.message?.includes('Failed to fetch') ||
                           checkoutError.message?.includes('Access-Control-Allow-Origin') ||
                           checkoutError.message?.includes('NetworkError') ||
                           checkoutError.message?.includes('Network request failed');
        
        const isTimeoutError = checkoutError.message?.includes('timed out') || 
                              checkoutError.message?.includes('timeout');
        
        let errorMessage: string;
        if (isTimeoutError) {
          errorMessage = checkoutError.message + '\n\n' +
            'Possible causes:\n' +
            '- Backend server is not responding\n' +
            '- Network connection issue\n' +
            '- CORS configuration blocking the request\n\n' +
            'Please try again or contact support if the problem persists.';
        } else if (isCorsError) {
          const currentOrigin = Platform.OS === 'web' && typeof window !== 'undefined' 
            ? window.location.origin 
            : 'your local development server';
          const isLocalhost = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1');
          
          if (isLocalhost) {
            errorMessage = `CORS Error: The backend server is not allowing requests from ${currentOrigin}.\n\n` +
              `This is a backend configuration issue. The Render backend needs to be configured to allow CORS requests from:\n` +
              `- ${currentOrigin} (for local development)\n` +
              `- https://bookapt.io (production domain)\n\n` +
              `Please contact support to configure CORS on the backend server, or try accessing from https://bookapt.io instead.`;
          } else {
            errorMessage = `CORS Error: The backend server is not allowing requests from ${currentOrigin}.\n\n` +
              `This is a backend configuration issue. The Render backend needs to be configured to allow CORS requests from:\n` +
              `- ${currentOrigin}\n` +
              `- https://bookapt.io (production domain)\n\n` +
              `Please contact support to configure CORS on the backend server.`;
          }
        } else {
          errorMessage = checkoutError.message || 'Failed to create checkout session. Please try again or contact support.';
        }
        
        // Use window.alert for web instead of Alert.alert
        if (Platform.OS === 'web') {
          window.alert(`Payment Error\n\n${errorMessage}\n\nIf this problem persists, please contact support.`);
        } else {
          Alert.alert(
            'Payment Error',
            `${errorMessage}\n\nIf this problem persists, please contact support.`,
            [{ text: 'OK' }]
          );
        }
      }
      
    } catch (error: any) {
      console.error('Purchase error:', error);
      
      // Always reset purchasing state, even if there's an error
      setPurchasing(false);
      
      // Use window.alert for web instead of Alert.alert
      if (Platform.OS === 'web') {
        window.alert(`Purchase Failed: ${error.message || 'Failed to process purchase'}`);
      } else {
        Alert.alert('Purchase Failed', error.message || 'Failed to process purchase');
      }
    } finally {
      // Ensure purchasing is always reset
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Loading credit packs...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Buy Credits</Text>
        <View style={{ width: 60 }} />
      </View>

      <ScrollView 
        style={styles.content} 
        scrollEnabled={true}
        showsVerticalScrollIndicator={true}
      >
        {/* Current Balance */}
        {userCredits && (
          <View style={styles.balanceCard}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text style={styles.balanceLabel}>Your Credits</Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {!isWeb && RNIap && (
                  <TouchableOpacity
                    onPress={handleRestorePurchases}
                    disabled={restoring}
                    style={[styles.restoreButton, restoring ? styles.restoreButtonDisabled : null]}
                  >
                    {restoring ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={styles.restoreButtonText}>Restore</Text>
                    )}
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  onPress={async () => {
                    console.log('🔄 Manual refresh triggered');
                    await creditsService.clearCreditsCache();
                    await loadUserCredits(true);
                  }}
                  style={{ padding: 8, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 4 }}
                >
                  <Text style={{ fontSize: 12, color: '#fff' }}>🔄 Refresh</Text>
                </TouchableOpacity>
              </View>
            </View>
            <Text style={styles.balanceAmount}>{userCredits.availableCredits}</Text>
            <Text style={styles.balanceSubtext}>
              {userCredits.freeCreditsRemaining > 0
                ? `${userCredits.freeCreditsRemaining} free + ${userCredits.purchasedCredits} purchased`
                : `${userCredits.purchasedCredits} purchased`}
            </Text>
          </View>
        )}

        {/* Credit Packs */}
        <View style={styles.packsContainer}>
          <Text style={styles.sectionTitle}>Choose a Credit Pack</Text>

          {creditPacks.map((pack) => {
            const isSelected = selectedPack?.id === pack.id;
            const isBestValue = pack.id === 'pack100';
            const isMostPopular = pack.id === 'pack40';

            return (
              <TouchableOpacity
                key={pack.id}
                style={[styles.packCard, isSelected && styles.packCardSelected]}
                onPress={() => handlePackSelect(pack)}
                disabled={purchasing}
              >
                {isMostPopular && (
                  <View style={styles.popularBadge}>
                    <Text style={styles.popularBadgeText}>MOST POPULAR</Text>
                  </View>
                )}
                {isBestValue && (
                  <View style={styles.bestValueBadge}>
                    <Text style={styles.bestValueText}>BEST VALUE</Text>
                  </View>
                )}

                <View style={styles.packHeader}>
                  <Text style={styles.packName}>{pack.name}</Text>
                  <Text style={styles.packPrice}>${pack.price.toFixed(2)}</Text>
                </View>

                <View style={styles.packDetails}>
                  <Text style={styles.packCredits}>{pack.credits} Credits</Text>
                  <Text style={styles.packPerBooking}>
                    ${pack.pricePerCredit.toFixed(2)} per booking
                  </Text>
                  <Text style={styles.packDescription}>{pack.description}</Text>
                </View>

                {isSelected && (
                  <View style={styles.selectedIndicator}>
                    <Text style={styles.selectedText}>Selected</Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Direct Payment Buttons - No Modal Needed */}
        {selectedPack && (
          <View style={styles.paymentButtonsContainer}>
            {/* Pay with Card (Stripe) - web only. On mobile we use only IAP per App Store / Play Store policy for digital goods. */}
            {isWeb && (
              <TouchableOpacity
                style={[styles.cardPaymentButton, purchasing && styles.paymentButtonDisabled]}
                onPress={() => handleWebPurchase()}
                disabled={purchasing}
              >
                {purchasing ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <>
                    <Text style={styles.cardPaymentButtonText}>
                      💳 Pay with Card
                    </Text>
                    <Text style={styles.cardPaymentButtonSubtext}>
                      Apple Pay • Google Pay • Cards
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}

            {/* In-App Purchase - mobile only (required for digital goods per App Store / Play Store). Always show on mobile so user sees a button; if RNIap didn't load we show message on press. */}
            {!isWeb && (
              <TouchableOpacity
                style={[styles.iapButton, purchasing && styles.paymentButtonDisabled]}
                onPress={() => {
                  if (!RNIap) {
                    Alert.alert(
                      'In-App Purchase Unavailable',
                      'In-app purchases are not available in this build. Please update the app from the App Store, or use the website at bookapt.io to buy credits with a card.'
                    );
                    return;
                  }
                  handleMobilePurchase(selectedPack);
                }}
                disabled={purchasing}
              >
                <Text style={styles.iapButtonText}>
                  {selectedPack ? `$${selectedPack.price.toFixed(2)}` : 'Buy'}
                </Text>
                <Text style={styles.iapButtonSubtext}>
                  Buy
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Info */}
        <View style={styles.infoCard}>
          <Text style={styles.infoTitle}>How Credits Work</Text>
          <Text style={styles.infoText}>
            • New users get 5 free credits to start{'\n'}
            • Each appointment booking costs 1 credit{'\n'}
            • Credits never expire{'\n'}
            • You can purchase more credits anytime
          </Text>
        </View>
      </ScrollView>

      {purchasing && (
        <View style={styles.purchasingOverlay}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.purchasingText}>Processing purchase...</Text>
        </View>
      )}

    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  content: {
    flex: 1,
  },
  balanceCard: {
    backgroundColor: '#007AFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: 8,
  },
  balanceAmount: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 4,
  },
  balanceSubtext: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  restoreButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: 'rgba(255,255,255,0.25)',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.6)',
    minWidth: 72,
    alignItems: 'center',
    justifyContent: 'center',
  },
  restoreButtonDisabled: {
    opacity: 0.6,
  },
  restoreButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
  },
  packsContainer: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  packCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    position: 'relative',
  },
  packCardSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F7FF',
  },
  bestValueBadge: {
    position: 'absolute',
    top: -8,
    right: 16,
    backgroundColor: '#34C759',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  bestValueText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  popularBadge: {
    position: 'absolute',
    top: -8,
    left: 16,
    backgroundColor: '#007AFF',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  popularBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  packHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  packName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  packPrice: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#007AFF',
  },
  packDetails: {
    marginTop: 8,
  },
  packCredits: {
    fontSize: 16,
    color: '#666',
    marginBottom: 4,
  },
  packPerBooking: {
    fontSize: 15,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 4,
  },
  packDescription: {
    fontSize: 14,
    color: '#999',
  },
  selectedIndicator: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    alignItems: 'center',
  },
  selectedText: {
    color: '#007AFF',
    fontWeight: '600',
  },
  cardInputContainer: {
    backgroundColor: '#fff',
    margin: 16,
    padding: 16,
    borderRadius: 12,
  },
  cardInfoBox: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    marginHorizontal: 20,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  cardInfoText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
  },
  cardInputTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  cardFieldWrapper: {
    height: 50,
    marginBottom: 16,
  },
  cardField: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
  },
  purchaseButton: {
    backgroundColor: '#007AFF',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  purchaseButtonDisabled: {
    opacity: 0.6,
  },
  purchaseButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  purchaseButtonContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  paymentButtonsContainer: {
    padding: 20,
    paddingBottom: 32,
    gap: 12,
  },
  cardPaymentButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  cardPaymentButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  cardPaymentButtonSubtext: {
    color: '#fff',
    fontSize: 13,
    opacity: 0.9,
  },
  iapButton: {
    backgroundColor: '#34C759',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#34C759',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  iapButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  iapButtonSubtext: {
    color: '#fff',
    fontSize: 13,
    opacity: 0.9,
  },
  paymentButtonDisabled: {
    opacity: 0.6,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  paymentModal: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 40,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalCloseText: {
    fontSize: 24,
    color: '#666',
    fontWeight: '300',
  },
  modalSummary: {
    padding: 20,
    backgroundColor: '#F8F9FA',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalSummaryText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
    textAlign: 'center',
  },
  modalContent: {
    padding: 20,
  },
  paymentOptionsContainer: {
    backgroundColor: '#fff',
    margin: 16,
    padding: 16,
    borderRadius: 12,
  },
  paymentOptionsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  paymentInfoBox: {
    backgroundColor: '#F0F7FF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#E0E8F0',
  },
  paymentInfoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1A1A1A',
    marginBottom: 8,
  },
  paymentInfoText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  payNowButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    paddingVertical: 18,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  payNowButtonDisabled: {
    opacity: 0.6,
  },
  payNowButtonContent: {
    alignItems: 'center',
  },
  payNowButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  payNowButtonSubtext: {
    color: '#fff',
    fontSize: 14,
    opacity: 0.9,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#e0e0e0',
  },
  dividerText: {
    marginHorizontal: 12,
    fontSize: 14,
    color: '#999',
  },
  infoCard: {
    backgroundColor: '#E8F5E8',
    margin: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#34C759',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2E7D32',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#2E7D32',
    lineHeight: 20,
  },
  purchasingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  purchasingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#fff',
  },
});

export default CreditsPurchaseScreen;

